import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Search, Loader2, CheckCircle2, XCircle, Clock } from "lucide-react";
import { format, parseISO } from "date-fns";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { StayHeader } from "@/components/stays/StayHeader";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import Footer from "@/components/Footer";
import { getBooking, getCancelQuote, cancelBooking, friendlyError, fmtAmount } from "@/lib/stayflowApi";
import { setMeta } from "@/lib/seo";

const fmtDate = (s) => { try { return format(parseISO(s), "dd MMM yyyy"); } catch { return s; } };
const fmtMoney = (n) =>
  n === null || n === undefined || isNaN(n)
    ? null
    : new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n);
const noticeUrl = (r = {}) =>
  r.cancellation_notice_url || r.cancel_notice_url || r.notice_url || r.cancellation_url || r.public_url || null;

const StatusBadge = ({ status }) => {
  const map = {
    confirmed: { c: "bg-green-100 text-green-700", I: CheckCircle2, t: "Confirmed" },
    paid: { c: "bg-green-100 text-green-700", I: CheckCircle2, t: "Paid" },
    cancelled: { c: "bg-red-100 text-red-700", I: XCircle, t: "Cancelled" },
    pending_payment: { c: "bg-amber-100 text-amber-700", I: Clock, t: "Pending payment" },
  };
  const s = map[status] || { c: "bg-paper-muted text-ink-soft", I: Clock, t: status || "Unknown" };
  const I = s.I;
  return <span className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold ${s.c}`} data-testid="lookup-status-badge"><I className="w-3.5 h-3.5" /> {s.t}</span>;
};

const BookingLookup = () => {
  const [bookingId, setBookingId] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [booking, setBooking] = useState(null);
  const [quote, setQuote] = useState(null);
  const [quoting, setQuoting] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [cancelResult, setCancelResult] = useState(null);

  useEffect(() => { setMeta({ title: "Find my booking — StayFlow" }); }, []);

  useEffect(() => {
    try {
      const s = JSON.parse(localStorage.getItem("sf_last_booking") || "null");
      if (s?.id) { setBookingId(s.id); if (s.phone) setPhone(s.phone); }
    } catch (e) {
      console.warn("Couldn't restore last booking from storage:", e);
    }
  }, []);

  const lookup = async (e) => {
    e?.preventDefault();
    if (!bookingId.trim() || (!phone.trim() && !email.trim())) { toast.error("Enter your booking ID and the phone number or email you booked with."); return; }
    setLoading(true); setBooking(null);
    try {
      const b = await getBooking(bookingId.trim(), {
        guest_phone: phone.trim().replace(/\s/g, "") || undefined,
        guest_email: email.trim() || undefined,
      });
      setBooking(b);
    } catch (err) {
      toast.error(friendlyError(err));
    } finally {
      setLoading(false);
    }
  };

  // Step 1 — fetch the live refund quote, then open the dialog with its exact message.
  const startCancel = async () => {
    setQuoting(true);
    try {
      const q = await getCancelQuote(booking.id);
      setQuote(q?.requires_confirmation ? q : null);
    } catch (e) {
      setQuote(null); // unpaid/offline bookings (or no quote available) cancel as before
    } finally {
      setQuoting(false);
      setDialogOpen(true);
    }
  };

  // Step 2 — confirm with the figures the guest actually saw.
  const doCancel = async () => {
    setCancelling(true);
    try {
      const res = await cancelBooking(
        booking.id,
        quote ? { shown_refund: quote.shown?.refund, shown_fee: quote.shown?.fee } : null
      );
      setDialogOpen(false);
      if (res?.already_cancelled) {
        toast.success("This booking is already cancelled");
        setBooking((b) => ({ ...b, status: "cancelled" }));
        return;
      }
      setCancelResult(res || {});
      setBooking((b) => ({ ...b, status: res?.status || "cancelled" }));
      toast.success("Booking cancelled");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const code = detail?.code;
      if (err?.response?.status === 422 && (code === "confirmation_required" || code === "confirmation_mismatch") && detail?.quote) {
        // Not an error — re-show the dialog with the fresh figures and let the guest confirm again.
        setQuote(detail.quote);
        setDialogOpen(true);
      } else {
        toast.error(friendlyError(err));
      }
    } finally {
      setCancelling(false);
    }
  };

  const canCancel = booking && !["cancelled"].includes(booking.status);

  return (
    <div className="bg-paper min-h-screen" data-testid="booking-lookup-page">
      <StayHeader />
      <main className="max-w-lg mx-auto px-5 py-14 md:py-20">
        <p className="overline text-brand">Manage booking</p>
        <h1 className="mt-4 font-heading font-extrabold text-3xl md:text-4xl tracking-tight text-ink">Find my booking</h1>
        <p className="mt-3 text-ink-soft">Enter your booking ID and the phone number or email you booked with.</p>

        <form onSubmit={lookup} className="mt-8 space-y-4" data-testid="lookup-form">
          <div><Label htmlFor="bid" className="text-xs text-ink-soft">Booking ID</Label><Input id="bid" value={bookingId} onChange={(e) => setBookingId(e.target.value)} placeholder="e.g. bk_1234" data-testid="lookup-id" className="mt-1 h-12 focus-visible:ring-brand" /></div>
          <div><Label htmlFor="ph" className="text-xs text-ink-soft">Phone number</Label><Input id="ph" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+91 98765 43210" data-testid="lookup-phone" className="mt-1 h-12 focus-visible:ring-brand" /></div>
          <div><Label htmlFor="em" className="text-xs text-ink-soft">or Email</Label><Input id="em" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@email.com" data-testid="lookup-email" className="mt-1 h-12 focus-visible:ring-brand" /></div>
          <button type="submit" disabled={loading} data-testid="lookup-submit" className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-ink text-white font-medium h-12 disabled:opacity-60">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />} Find booking
          </button>
        </form>

        {booking && (
          <div className="mt-8 rounded-3xl border border-black/5 bg-white p-6" data-testid="lookup-result">
            <div className="flex items-center justify-between">
              <h2 className="font-heading font-bold text-lg text-ink">Booking details</h2>
              <StatusBadge status={booking.status} />
            </div>
            <div className="mt-4 space-y-2 text-sm">
              <div className="flex justify-between"><span className="text-ink-soft">Booking ID</span><span className="font-mono text-ink">{booking.id}</span></div>
              {booking.guest_name && <div className="flex justify-between"><span className="text-ink-soft">Guest</span><span className="text-ink">{booking.guest_name}</span></div>}
              {booking.check_in && <div className="flex justify-between"><span className="text-ink-soft">Check-in</span><span className="text-ink font-medium">{fmtDate(booking.check_in)}</span></div>}
              {booking.check_out && <div className="flex justify-between"><span className="text-ink-soft">Check-out</span><span className="text-ink font-medium">{fmtDate(booking.check_out)}</span></div>}
              {(booking.amount_paid > 0 || booking.amount_payable != null) && (
                <div className="flex justify-between">
                  <span className="text-ink-soft">{booking.amount_paid > 0 ? "Amount paid" : "Total"}</span>
                  <span className="font-heading font-bold text-ink" data-testid="lookup-amount">{fmtAmount(booking.amount_paid > 0 ? booking.amount_paid : booking.amount_payable)}</span>
                </div>
              )}
            </div>

            {booking.status === "pending_payment" && booking.payment_link_url && (
              <a href={booking.payment_link_url} className="mt-5 w-full inline-flex items-center justify-center rounded-full bg-brand text-white font-medium h-11" data-testid="lookup-pay">Complete payment</a>
            )}

            {cancelResult && (
              <div className="mt-5 rounded-2xl border border-red-100 bg-red-50/60 p-4" data-testid="cancel-result">
                <p className="font-heading font-bold text-ink">Booking cancelled</p>
                <div className="mt-2 space-y-1.5 text-sm">
                  <div className="flex justify-between"><span className="text-ink-soft">Refund amount</span><span className="font-medium text-ink" data-testid="cancel-refund-amount">{fmtMoney(cancelResult.refund_amount) ?? "₹0.00"}</span></div>
                  <div className="flex justify-between"><span className="text-ink-soft">Retained</span><span className="font-medium text-ink" data-testid="cancel-retained-amount">{fmtMoney(cancelResult.retained_amount) ?? "₹0.00"}</span></div>
                  {cancelResult.refund_status && <div className="flex justify-between"><span className="text-ink-soft">Refund status</span><span className="text-ink">{cancelResult.refund_status}</span></div>}
                </div>
                {noticeUrl(cancelResult) && (
                  <a href={noticeUrl(cancelResult)} target="_blank" rel="noreferrer" className="mt-3 inline-block text-sm font-medium text-brand underline" data-testid="cancel-notice-link">View cancellation notice</a>
                )}
              </div>
            )}

            {canCancel && (
              <AlertDialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <button onClick={startCancel} disabled={quoting} className="mt-4 w-full rounded-full border border-red-200 text-red-600 font-medium h-11 hover:bg-red-50 transition-colors disabled:opacity-60" data-testid="cancel-trigger">
                  {quoting ? "Checking refund…" : "Cancel booking"}
                </button>
                <AlertDialogContent data-testid="cancel-dialog">
                  <AlertDialogHeader>
                    <AlertDialogTitle>Cancel booking</AlertDialogTitle>
                    <AlertDialogDescription data-testid="cancel-dialog-message">
                      {quote?.message_guest || `This will cancel booking ${booking.id}. This action cannot be undone.`}
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  {quote?.terms && <p className="text-xs text-ink-soft" data-testid="cancel-dialog-terms">{quote.terms}</p>}
                  <AlertDialogFooter>
                    <AlertDialogCancel data-testid="cancel-dismiss">{quote?.buttons?.keep || "Keep booking"}</AlertDialogCancel>
                    <AlertDialogAction onClick={(e) => { e.preventDefault(); doCancel(); }} disabled={cancelling} className="bg-red-600 hover:bg-red-700 disabled:opacity-60" data-testid="cancel-confirm">
                      {cancelling ? "Cancelling…" : (quote?.buttons?.confirm || "Cancel booking")}
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            )}
          </div>
        )}

        <p className="mt-8 text-center text-sm text-ink-soft">Looking to book? <Link to="/stays" className="text-brand font-medium">Browse stays</Link></p>
      </main>
      <Footer />
    </div>
  );
};

export default BookingLookup;
